<template>
  <div class="detail-container">
    <!-- 返回按钮 -->
    <button @click="goBack" class="back-button">返回</button>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <p>加载失败：{{ error }}</p>
      <button @click="fetchDetailData" class="retry-button">重试</button>
    </div>

    <!-- 内容区域 -->
    <div v-else-if="validImageUrls.length > 0" class="content-container">
      <!-- 从上往下顺序展示所有图片 -->
      <div v-for="(image, index) in validImageUrls" :key="index" class="detail-image-item">
        <img 
          :src="trimImageUrl(image)" 
          :alt="`Detail Image ${index + 1}`"
        >
      </div>
    </div>

    <!-- 无数据状态 -->
    <div v-else class="empty-container">
      <p>暂无数据</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

// 定义菜单详情数据类型
interface MenuDetailImage {
  id: number;
  imgUrl: string;
  imgUrlen: string;
}

// 定义API响应的数据类型
interface ApiResponse {
  code: number;
  result: MenuDetailImage[];
  message: string;
}

export default defineComponent({
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { locale } = useI18n();
    const menuDetailImages = ref<MenuDetailImage[]>([]);
    const loading = ref<boolean>(false);
    const error = ref<string>('');

    // 获取当前语言
    const currentLanguage = computed(() => locale.value);

    // 根据当前语言选择正确的图片URL
    const validImageUrls = computed(() => {
      if (!menuDetailImages.value || menuDetailImages.value.length === 0) return [];
      
      const urls: string[] = [];
      for (const image of menuDetailImages.value) {
        const url = currentLanguage.value === 'en' ? image.imgUrlen : image.imgUrl;
        if (url) {
          urls.push(url);
        }
      }
      return urls;
    });

    // 获取详情数据的方法
    const fetchDetailData = async () => {
      // 首先检查路由参数中是否有传递的数据
      const menuDetailParam = route.query.menuDetail as string;
      if (menuDetailParam) {
        try {
          // 从路由参数中解析数据
          const parsedData = JSON.parse(menuDetailParam);
          menuDetailImages.value = parsedData;
          return;
        } catch (err) {
          console.error('解析路由参数中的菜单详情数据失败:', err);
          // 解析失败，继续调用接口获取数据
        }
      }

      const menuId = route.query.menuId as string;
      if (!menuId) {
        error.value = '参数错误';
        return;
      }

      loading.value = true;
      error.value = '';
      
      try {
        // 调用菜单详情接口
        const response = await fetch(`/jeecg-boot/energycube/menu/queryMenuDetail?id=${menuId}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        
        // 检查响应代码
        if (data.code === 200) {
          menuDetailImages.value = data.result || [];
        } else {
          throw new Error(data.message || '获取详情数据失败');
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : '未知错误';
        console.error('获取详情数据失败:', err);
      } finally {
        loading.value = false;
      }
    };

    // 处理图片URL中的额外字符
    const trimImageUrl = (url: string): string => {
      // 移除字符串两端的空格、反引号等特殊字符
      return url.replace(/^[\s`]+|[\s`]+$/g, '');
    };

    // 返回上一页
    const goBack = () => {
      router.back();
    };

    // 监听路由参数变化，当menuId改变时重新获取数据
    watch(
      () => route.query.menuId,
      (newMenuId, oldMenuId) => {
        if (newMenuId !== oldMenuId) {
          fetchDetailData();
        }
      }
    );

    // 监听语言变化，切换图片
    watch(
      () => locale.value,
      () => {
        // 语言变化时，图片URL会自动通过validImageUrls计算属性更新
        console.log('Language changed, updating images');
      }
    );

    // 组件挂载时获取数据
    onMounted(() => {
      fetchDetailData();
    });

    return {
      menuDetailImages,
      loading,
      error,
      validImageUrls,
      fetchDetailData,
      trimImageUrl,
      goBack,
      currentLanguage
    };
  }
});
</script>

<style scoped>
.detail-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.back-button {
  align-self: flex-start;
  padding: 8px 16px;
  background-color: #12aa4a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 80px; /* 将按钮向下移动，避免被顶部导航栏遮挡 */
  margin-bottom: 20px;
  transition: background-color 0.3s;
}

.back-button:hover {
  background-color: #0e8e3e;
}

.content-container {
  width: 100%;
  max-width: 1200px;
}

/* MollyProductDetail风格的布局样式 */
.productDetail_one {
  width: 90%;
  border-radius: 20px;
  margin: 0 auto;
  overflow: hidden; /* 防止图片溢出容器 */
}

.productDetail_two {
  width: 90%;
  border-radius: 20px;
  margin: 10px auto;
  display: flex;
  overflow: hidden; /* 防止图片溢出容器 */
}

.two_left {
  width: 50%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.two_left_top {
  width: 100%;
  height: 50%;
  border-radius: 20px;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.two_left_bottom {
  width: 100%;
  height: 50%;
  border-radius: 20px;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.two_right {
  width: 50%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
}


.productDetail_three {
  width: 90%;
  margin: 10px auto;
  overflow: hidden; /* 防止图片溢出容器 */
}

.three_top {
  width: 100%;
  height: 15%;
  border-radius: 20px;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.three_middle {
  width: 100%;
  height: 44%;
  border-radius: 20px;
  margin-top: 1%;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.three_middle_left {
  width: 49%;
  height: 100%;
  border-radius: 20px;
  float: left;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.three_middle_right {
  width: 49%;
  height: 100%;
  border-radius: 20px;
  float: right;
  overflow: hidden; /* 新增：防止图片溢出容器 */
}

.three_bottom {
  width: 100%;
  height: 39%;
  border-radius: 20px;
  margin-top: 1%;
  overflow: hidden;
}

/* 图片样式 */
.productDetail_one > img,
.two_left_top > img,
.two_left_bottom > img,
.two_right > img,
.three_top > img,
.three_middle_left > img,
.three_middle_right > img,
.three_bottom > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
  transition: all 0.3s ease;
}

.productDetail_one > img:hover,
.two_left_top > img:hover,
.two_left_bottom > img:hover,
.two_right > img:hover,
.three_top > img:hover,
.three_middle_left > img:hover,
.three_middle_right > img:hover,
.three_bottom > img:hover {
  transform: scale(1.02);
}

/* 图片样式 */
.detail-image-item {
  width: 90%;
  margin: 0 auto 20px;
  border-radius: 20px;
  overflow: hidden;
  background-color: transparent;
}

.detail-image-item img {
  width: 100%;
  height: auto;
  max-width: 100%;
  object-fit: contain;
  border-radius: 20px;
  transition: transform 0.3s ease;
}

.detail-image-item img:hover {
  transform: scale(1.02);
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #12aa4a;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误状态样式 */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
  color: #ff4d4f;
}

.retry-button {
  margin-top: 16px;
  padding: 6px 16px;
  background-color: #12aa4a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #0e8e3e;
}

/* 空数据状态样式 */
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  width: 100%;
  color: #999;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .productDetail_two {
    flex-direction: column;
  }
  
  .two_left,
  .two_right {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .three_middle_left,
  .three_middle_right {
    float: none;
    width: 100%;
    margin-bottom: 10px;
  }
  
  .remaining-images {
    grid-template-columns: 1fr;
    gap: 15px;
  }
}
</style>